import {colors} from 'shells/colors/colors';
import {ParentCompanyIcon} from 'shells/icon';
import {CompanyRegularIcon} from 'shells/icon/svg/company-regular-icon';
import {LinkedEntity} from 'shells/linked-entity/linked-entity';
import {Body} from 'shells/typography/body';

import type {AccountPageFragment} from 'nutshell-graphql-types';

import {getAccountHierarchyParentContext} from './helpers';

interface Props {
    account: AccountPageFragment;
    isMiniHeader?: boolean;
}

export function AccountContextOverview(props: Props) {
    const {account, isMiniHeader = false} = props;

    const childrenCount = account.childAccounts.totalCount ?? 0;
    const hasChildren = childrenCount > 0;
    const hasParentAccount = account.parentAccount !== null;

    const Icon = hasChildren ? ParentCompanyIcon : CompanyRegularIcon;

    // When in a mini header state, if there is no uploaded avatar, no need to
    // render side by side icons
    const showIcon = isMiniHeader ? account.hasUploadedAvatar : true;
    const hasHierarchy = hasParentAccount || hasChildren;

    // Context is only useful when there is extra company hierarchy info to
    // display
    if (!hasHierarchy || !NutClientConfig.hasAccountHierarchy) {
        return null;
    }

    return (
        <div className='flex items-center gap-8'>
            {showIcon && (
                <Icon
                    size={20}
                    // To get icons to properly align, handling special case for parent company
                    // icon that doesn't have the same whitespace issue
                    style={{color: colors.company, marginLeft: hasChildren ? 0 : -3}}
                    alignCenter={true}
                />
            )}
            <Body>
                {hasParentAccount ? (
                    <>
                        Child company of{' '}
                        <Body isBold={true}>
                            <LinkedEntity
                                type='accounts'
                                name={account.parentAccount?.name ?? 'Parent'}
                                htmlUrlPath={account.parentAccount?.htmlUrlPath}
                                shouldClientRoute={true}
                                useEntityColor={false}
                                displayInline={true}
                                allowWrap={true}
                            />
                        </Body>
                    </>
                ) : (
                    getAccountHierarchyParentContext(childrenCount)
                )}
            </Body>
        </div>
    );
}
